import { Alert, Box, Button, Grid, Link, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { customerStatusLabels } from '@/features/operations/labels';
import {
  isLookupSettled,
  quotationLookupCopy,
  type CounterLookupCopy,
  type AccountLookup,
  type CounterContactErrors,
  type CounterContactValues,
} from '@/features/customers/counterLookup';

/**
 * The contact of a counter quotation (ADR 0008, #229). The mail comes first because it is what
 * finds the account: found, the employee confirms name and phone with the person; not found,
 * registering creates a pending account and sends its invitation. Either way the employee
 * knows which of the two is about to happen before registering.
 */
export function CounterAccountLookup({
  values,
  errors,
  lookup,
  onChange,
  onLookup,
  copy = quotationLookupCopy,
}: {
  values: CounterContactValues;
  errors: CounterContactErrors;
  lookup: AccountLookup;
  onChange: (field: keyof CounterContactValues, value: string) => void;
  onLookup: () => void;
  /// Who the person is and what is about to be registered: a quotation by default.
  copy?: CounterLookupCopy;
}) {
  const settled = isLookupSettled(lookup);
  const waitingHelper = 'Buscá primero la cuenta por mail.';

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          type="email"
          label={copy.emailLabel}
          value={values.email}
          error={Boolean(errors.email)}
          helperText={errors.email || 'Con el mail buscamos si ya tiene cuenta.'}
          onChange={(event) => onChange('email', event.target.value)}
          onBlur={() => {
            if (values.email.trim() && lookup.status === 'idle') {
              onLookup();
            }
          }}
          onKeyDown={(event) => {
            // Enter searches instead of submitting a form that is not complete yet.
            if (event.key === 'Enter') {
              event.preventDefault();
              onLookup();
            }
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ pt: { md: 1 } }}>
          <Button
            variant="outlined"
            onClick={onLookup}
            disabled={lookup.status === 'searching' || !values.email.trim()}
          >
            {lookup.status === 'searching' ? 'Buscando...' : 'Buscar cuenta'}
          </Button>
        </Box>
      </Grid>

      {lookup.status === 'found' ? (
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>{lookup.customer.fullName}</strong> ya tiene cuenta (
              {(customerStatusLabels[lookup.customer.status] || '').toLowerCase()}).{' '}
              {copy.foundNotice}{' '}
              <Link
                component={RouterLink}
                to={`/admin/customers/${lookup.customer.id}`}
                target="_blank"
                rel="noopener"
              >
                Ver la ficha
              </Link>
            </Typography>
          </Alert>
        </Grid>
      ) : null}
      {lookup.status === 'none' ? (
        <Grid item xs={12}>
          <Alert severity="info">
            No hay una cuenta con este mail. {copy.noneNotice} {lookup.email}.
          </Alert>
        </Grid>
      ) : null}
      {lookup.status === 'failed' ? (
        <Grid item xs={12}>
          <Alert severity="warning">{copy.failedNotice}</Alert>
        </Grid>
      ) : null}

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label={copy.nameLabel}
          value={values.fullName}
          disabled={!settled}
          error={Boolean(errors.fullName)}
          helperText={
            errors.fullName ||
            (!settled
              ? waitingHelper
              : lookup.status === 'found'
                ? copy.foundNameHelper
                : undefined)
          }
          onChange={(event) => onChange('fullName', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          type="tel"
          label="Teléfono"
          value={values.phone}
          disabled={!settled}
          error={Boolean(errors.phone)}
          helperText={errors.phone || (!settled ? waitingHelper : 'Por ejemplo 099 123 456.')}
          onChange={(event) => onChange('phone', event.target.value)}
        />
      </Grid>
    </Grid>
  );
}
